/**
 * Cliente HTTP da API.
 *
 * A API é servida sob `/api` no mesmo host do front — em desenvolvimento pelo
 * proxy do Vite, em produção pelo mesmo Nginx. Não há CORS nem cookie
 * cross-subdomain em lugar nenhum.
 */

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly mensagem: string,
    readonly detalhes?: unknown,
  ) {
    super(mensagem);
    this.name = "ErroApi";
  }
}

type Metodo = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface Opcoes {
  metodo?: Metodo;
  corpo?: unknown;
  /** Query string; chaves com valor `undefined` são omitidas. */
  params?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

const BASE = "/api";

/** Disparado quando a sessão expira, para que a aplicação redirecione ao login. */
export const EVENTO_NAO_AUTENTICADO = "soap:nao-autenticado";

function montarUrl(caminho: string, params?: Opcoes["params"]) {
  const url = new URL(`${BASE}${caminho}`, window.location.origin);
  for (const [chave, valor] of Object.entries(params ?? {})) {
    if (valor !== undefined) url.searchParams.set(chave, String(valor));
  }
  return url.toString();
}

/**
 * Renovação de sessão. Uma única renovação corre por vez: requisições
 * paralelas que recebem 401 aguardam a mesma promessa em vez de dispararem
 * N renovações concorrentes.
 */
let renovacaoEmCurso: Promise<boolean> | null = null;

async function renovarSessao(): Promise<boolean> {
  renovacaoEmCurso ??= fetch(`${BASE}/auth/refresh`, {
    method: "POST",
    credentials: "include",
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      renovacaoEmCurso = null;
    });
  return renovacaoEmCurso;
}

export async function api<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const { metodo = "GET", corpo, params, signal } = opcoes;

  const executar = () =>
    fetch(montarUrl(caminho, params), {
      method: metodo,
      credentials: "include",
      signal,
      headers: corpo ? { "Content-Type": "application/json" } : undefined,
      body: corpo ? JSON.stringify(corpo) : undefined,
    });

  let resposta = await executar();

  if (resposta.status === 401 && !caminho.startsWith("/auth/")) {
    if (await renovarSessao()) {
      resposta = await executar();
    } else {
      window.dispatchEvent(new CustomEvent(EVENTO_NAO_AUTENTICADO));
      throw new ErroApi(401, "Sessão expirada");
    }
  }

  if (resposta.status === 204) return undefined as T;

  const conteudo = resposta.headers.get("content-type") ?? "";
  const bruto = conteudo.includes("application/json") ? null : await resposta.text();
  // O Nest devolve corpo vazio quando o handler retorna null. Sem isto, o
  // chamador recebe "" — que não é nullish, então `x?.campo` não curto-circuita.
  const dados = bruto === null ? await resposta.json() : bruto === "" ? null : bruto;

  if (!resposta.ok) {
    const mensagem =
      typeof dados === "object" && dados !== null && "message" in dados
        ? String((dados as { message: unknown }).message)
        : `Erro ${resposta.status}`;
    throw new ErroApi(resposta.status, mensagem, dados);
  }

  return dados as T;
}

export const get = <T>(caminho: string, params?: Opcoes["params"]) => api<T>(caminho, { params });
export const post = <T>(caminho: string, corpo?: unknown) =>
  api<T>(caminho, { metodo: "POST", corpo });
export const patch = <T>(caminho: string, corpo?: unknown) =>
  api<T>(caminho, { metodo: "PATCH", corpo });
export const del = <T>(caminho: string) => api<T>(caminho, { metodo: "DELETE" });
